import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { fetchCrossPostHistory } from "../../client/manager/cross-posting";
import type { CrossPostRow, Pagination } from "../../models/ayrshare";
import { crossPostingQueryKeys } from "./queryKeys";

type CrossPostHistoryResult = {
  posts: CrossPostRow[];
  pagination: Pagination;
};

/**
 * Paginated history of cross-posts for a comedian.
 *
 * Page-based (NOT infinite) per CONTEXT.md D-04 — if a later phase needs a
 * mobile feed, add a sibling `useInfiniteCrossPostHistory` hook (~15 lines).
 *
 * `page` is zero-indexed (server clamps Math.max(0, …)) and `pageSize` is the
 * parameter name (server reads `pageSize`, NOT `per_page`). PATTERNS.md
 * deviations #1 + #2.
 *
 * staleTime 30s per CONTEXT.md D-03 (user posts and wants to see it quickly).
 */
export function useCrossPostHistory(
  comedianId: string,
  page: number = 0,
  pageSize: number = 20
): UseQueryResult<CrossPostHistoryResult, Error> {
  return useQuery({
    queryKey: crossPostingQueryKeys.crossPostHistory(
      comedianId,
      page,
      pageSize
    ),
    queryFn: () => fetchCrossPostHistory(comedianId, page, pageSize),
    enabled: !!comedianId,
    staleTime: 30 * 1000,
  });
}
